/**
 * Turns one model's prose into blocks the report can lay out.
 *
 * The models are asked for bullet points and they return them — as text, with
 * hyphens and numbers and the odd **emphasis**. Dropping that into a single
 * paragraph threw all of it away: five sections of clinical advice arrived as
 * one unbroken grey wall, in a document whose whole job is to be read.
 *
 * Deliberately a small formatter and not a Markdown engine. The input is one
 * model's output, it goes straight into a clinical report, and a parser that
 * renders arbitrary markup — links, images, raw HTML — is a wider door than
 * this needs to open.
 */

const BULLET = /^\s*(?:[-*\u2022\u00b7]|\d+[.)])\s+/;

/** A bullet marker with nothing after it — a stray "-" is not a list item. */
const EMPTY_BULLET = /^(?:[-*\u2022\u00b7]|\d+[.)])$/;

/**
 * A short line ending in a colon is a lead-in to what follows, and the models
 * write plenty of them. Longer than this and it is a sentence that happens to
 * end in a colon, which is prose.
 */
const HEADING_MAX = 60;

/** Groups the lines into runs of bullets, headings and paragraph text. */
export function parseProse(text) {
  const blocks = [];
  for (const raw of String(text ?? '').split(/\r?\n/)) {
    const line = raw.trim();
    if (!line || EMPTY_BULLET.test(line)) continue;

    if (BULLET.test(line)) {
      const item = line.replace(BULLET, '').trim();
      if (!item) continue;
      const last = blocks[blocks.length - 1];
      if (last?.type === 'list') last.items.push(item);
      else blocks.push({ type: 'list', items: [item] });
      continue;
    }

    if (line.endsWith(':') && line.length <= HEADING_MAX) {
      blocks.push({ type: 'heading', text: line.replace(/:$/, '') });
      continue;
    }

    blocks.push({ type: 'paragraph', text: line });
  }
  return blocks;
}
